import type { JsonSchemaType, Object as PortalObject } from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import { useCustomer, useObjectCategory } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';

export interface ResolvedFieldValueProps {
  field: string;
  value: unknown;
  objectSchema?: JsonSchemaType;
  categorySchema?: JsonSchemaType;
  category?: PortalObject['category'];
  assignedCustomer?: PortalObject['assignedCustomer'];
}

export function ResolvedFieldValue({
  field,
  value,
  objectSchema,
  categorySchema,
  category,
  assignedCustomer,
}: ResolvedFieldValueProps) {
  const { t } = useIntl();

  const isRelationWithId = (
    input: unknown
  ): input is { id: string; name?: string; metadataSchema?: JsonSchemaType } => {
    return typeof input === 'object' && input !== null && 'id' in input;
  };
  // Always call hooks unconditionally to preserve the hook sequence.
  const effectiveAssignedCustomer = field === 'assignedCustomer' ? value : assignedCustomer;
  const assignedCustomerId =
    effectiveAssignedCustomer && isRelationWithId(effectiveAssignedCustomer)
      ? (effectiveAssignedCustomer.id ?? '')
      : typeof effectiveAssignedCustomer === 'string'
        ? effectiveAssignedCustomer
        : '';
  const { data: customer } = useCustomer(field === 'assignedCustomer' ? assignedCustomerId : '');
  const resolvedCustomerName =
    (effectiveAssignedCustomer && isRelationWithId(effectiveAssignedCustomer)
      ? effectiveAssignedCustomer.name
      : null) ?? customer?.name;

  const effectiveCategory = field === 'category' ? value : category;
  const categoryId =
    effectiveCategory && isRelationWithId(effectiveCategory)
      ? (effectiveCategory.id ?? '')
      : ((effectiveCategory as string | undefined) ?? '');
  const { data: categoryData } = useObjectCategory(categoryId, {
    disabled: field !== 'category' || !categoryId || isRelationWithId(effectiveCategory),
  });
  const resolvedCategoryName =
    (effectiveCategory && isRelationWithId(effectiveCategory) ? effectiveCategory.name : null) ??
    categoryData?.name;
  const resolvedCategorySchema =
    categorySchema ||
    (effectiveCategory && isRelationWithId(effectiveCategory)
      ? (effectiveCategory.metadataSchema as JsonSchemaType | undefined)
      : (categoryData?.metadataSchema as JsonSchemaType | undefined));

  if (value === null || value === undefined || value === '') {
    return <span className="font-medium">{t('activity.none')}</span>;
  }

  const needsMetadata = field.startsWith('dynamicMetadata.');

  if (!needsMetadata && field !== 'assignedCustomer' && field !== 'category') {
    return <span>{String(value)}</span>;
  }

  if (field === 'assignedCustomer' && resolvedCustomerName) {
    return <span>{resolvedCustomerName}</span>;
  }

  if (field === 'category' && resolvedCategoryName) {
    return <span>{resolvedCategoryName}</span>;
  }

  if (field.startsWith('dynamicMetadata.')) {
    const [, key] = field.split('.');
    const fieldDef =
      (objectSchema?.properties?.[key] as JsonSchemaType['properties'][string]) ||
      (resolvedCategorySchema?.properties?.[key] as JsonSchemaType['properties'][string]);

    if (fieldDef) {
      switch (fieldDef.type) {
        case 'date':
          return typeof value === 'string' && DateTime.fromISO(value).isValid ? (
            <span>{DateTime.fromISO(value).toFormat('dd.MM.yyyy')}</span>
          ) : (
            <span>{String(value)}</span>
          );
        case 'boolean':
          return <span>{t(value ? 'common.yes' : 'common.no')}</span>;
        case 'number':
          return <span>{typeof value === 'number' ? value.toString() : String(value)}</span>;
        default:
          return <span>{String(value)}</span>;
      }
    }
  }

  return <span>{String(value)}</span>;
}
